import { Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, FileText, KanbanSquare, Shield, Sparkles, TrendingUp, Users } from "lucide-react";
import { DEPARTMENTS } from "@/lib/types";
import { PageMeta } from "@/components/PageMeta";

export default function Index() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="GrowthforgeAi — AI-Powered Operations Platform"
        description="CRM, AI assistant, automations, reporting, and department planning tools — all in one secure, role-based platform."
        path="/"
      />
      {/* Nav */}
      <header className="border-b border-border/60 bg-background/80 backdrop-blur sticky top-0 z-30">
        <div className="container flex h-14 items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-semibold" aria-label="GrowthforgeAi home">
            <div className="h-7 w-7 rounded-md bg-gradient-hero shadow-pop flex items-center justify-center text-white text-xs font-bold">E</div>
            GrowthforgeAi
          </Link>
          <nav aria-label="Primary navigation" className="flex items-center gap-2">
            <Link to="/pricing" className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">Pricing</Link>
            <Button asChild variant="ghost" size="sm"><Link to="/login">Sign in</Link></Button>
            <Button asChild size="sm"><Link to="/login">Get started</Link></Button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="container py-20 sm:py-28 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-accent/40 px-3 py-1 text-xs text-accent-foreground mb-6">
          <Sparkles className="h-3.5 w-3.5" /> Multi-department operations, one dashboard
        </div>
        <h1 className="text-4xl sm:text-6xl font-bold tracking-tight max-w-3xl mx-auto leading-[1.05]">
          Run every department<br />
          <span className="bg-gradient-hero bg-clip-text text-transparent">from one place.</span>
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground mt-6 max-w-2xl mx-auto">
          Plan initiatives, generate marketing calendars, model trading risk, and architect digital products — all with one suite, one login, one source of truth.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
          <Button asChild size="lg" className="gap-2">
            <Link to="/login">Start free <ArrowRight className="h-4 w-4" /></Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <a href="#features">Explore features</a>
          </Button>
        </div>
      </section>

      {/* Departments */}
      <section id="features" className="container pb-16">
        <h2 className="text-2xl sm:text-3xl font-semibold text-center mb-2">Four departments. One platform.</h2>
        <p className="text-muted-foreground text-center mb-10">Each module ships with a guided generator and exportable reports.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {(Object.keys(DEPARTMENTS) as (keyof typeof DEPARTMENTS)[]).map((d) => (
            <Card key={d} className="shadow-elev hover:shadow-pop transition-shadow">
              <CardContent className="p-5">
                <div className={`h-1.5 w-12 rounded-full bg-gradient-to-r ${DEPARTMENTS[d].accent} mb-3`} />
                <h3 className="font-semibold text-sm mb-1">{DEPARTMENTS[d].label}</h3>
                <p className="text-xs text-muted-foreground">{deptBlurb(d)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Capabilities */}
      <section className="container py-16 border-t border-border/60">
        <div className="grid md:grid-cols-3 gap-6">
          <Feature icon={<FileText className="h-5 w-5" />} title="Reports & exports" body="Generate structured reports in seconds. Download as Markdown or PDF." />
          <Feature icon={<KanbanSquare className="h-5 w-5" />} title="Project kanban" body="Track cross-department initiatives from backlog to done in a single board." />
          <Feature icon={<TrendingUp className="h-5 w-5" />} title="Activity insights" body="See output per department, recent activity, and audit history at a glance." />
          <Feature icon={<Users className="h-5 w-5" />} title="Team roles" body="Admin and member roles with secure role-based access enforced server-side." />
          <Feature icon={<Shield className="h-5 w-5" />} title="Secure by default" body="Row-level security, OAuth sign-in, and audit logs on every sensitive action." />
          <Feature icon={<Sparkles className="h-5 w-5" />} title="Polished UI" body="A clean, fast, dark-first interface designed for daily operational use." />
        </div>
      </section>

      {/* CTA */}
      <section className="container py-20">
        <div className="rounded-2xl bg-gradient-hero p-10 sm:p-14 text-center text-white shadow-pop">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">Ready to operate at enterprise pace?</h2>
          <p className="text-white/85 mb-6 max-w-xl mx-auto">Create an account and explore the full suite in minutes.</p>
          <Button asChild size="lg" variant="secondary">
            <Link to="/login">Get started — it's free</Link>
          </Button>
        </div>
      </section>

      <footer className="border-t border-border/60 py-8 text-center text-xs text-muted-foreground space-y-2">
        <nav aria-label="Footer navigation" className="flex justify-center gap-4">
          <Link to="/pricing" className="hover:text-foreground">Pricing</Link>
          <Link to="/terms" className="hover:text-foreground">Terms</Link>
          <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
          <Link to="/refunds" className="hover:text-foreground">Refunds</Link>
        </nav>
        <div>© {new Date().getFullYear()} GrowthforgeAi. All rights reserved.</div>
      </footer>
    </div>
  );
}

function Feature({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="space-y-2">
      <div className="h-10 w-10 rounded-lg bg-accent text-accent-foreground flex items-center justify-center">{icon}</div>
      <h3 className="font-semibold">{title}</h3>
      <p className="text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function deptBlurb(d: keyof typeof DEPARTMENTS): string {
  switch (d) {
    case "project-director": return "Executive briefs, workstreams, budget, and risk planning.";
    case "marketing": return "Multi-week, multi-channel content calendars with KPIs.";
    case "trading": return "Position sizing, drawdown scenarios, risk controls.";
    case "revenue": return "Digital product blueprints with offer, plan, and economics.";
  }
}
